import React from 'react';
import Cropper from 'react-easy-crop';
import { Button, Modal } from 'semantic-ui-react';

type Area = { x: number; y: number; width: number; height: number };

async function createImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (e) => reject(e));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });
}

function getRadianAngle(deg: number) {
    return (deg * Math.PI) / 180;
}

async function getCroppedBlob(
    imageSrc: string,
    crop: Area,
    outputWidth: number,
    outputHeight: number,
    rotation = 0
): Promise<Blob> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas context not available');
    }

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // react-easy-crop's croppedAreaPixels are already in natural image pixel space — do not scale by image.width
    const rot = getRadianAngle(rotation);
    if (rotation !== 0) {
        ctx.translate(outputWidth / 2, outputHeight / 2);
        ctx.rotate(rot);
        ctx.translate(-outputWidth / 2, -outputHeight / 2);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
        image,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        outputWidth,
        outputHeight
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob as Blob), 'image/jpeg', 0.95);
    });
}

interface Props {
    open: boolean;
    imageSrc: string;
    onClose: () => void;
    /** May return a Promise so the modal stays in loading state during S3 uploads. */
    onCropped: (blob: Blob) => void | Promise<void>;
    /** For 1:1 square crop. Output is outputSize×outputSize. */
    outputSize?: number;
    /** Crop aspect ratio width÷height (e.g. 4 = 4:1, 4/3 = 4:3, 1 = square). */
    aspect?: number;
    outputWidth?: number;
    outputHeight?: number;
    /** Picked file size from `<input type="file">` — compared to cropped JPEG in console (before S3). */
    sourceFileSizeBytes?: number;
    /** Modal title; default depends on aspect */
    cropTitle?: string;
}

interface State {
    crop: { x: number; y: number };
    zoom: number;
    croppedAreaPixels?: Area;
    submitting: boolean;
    /** Mount Cropper after modal layout so react-easy-crop measures a non-zero area (fixes 4:3 in Modal). */
    cropperReady: boolean;
}

export default class ImageCropperModal extends React.Component<Props, State> {
    private cropMountTimer: number | undefined;

    state: State = {
        crop: { x: 0, y: 0 },
        zoom: 1,
        croppedAreaPixels: undefined,
        submitting: false,
        cropperReady: false,
    };

    componentDidMount() {
        if (this.props.open) {
            this.scheduleCropperMount();
        }
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.open && !prevProps.open) {
            this.scheduleCropperMount();
        }
        if (!this.props.open && prevProps.open) {
            if (this.cropMountTimer != null) {
                window.clearTimeout(this.cropMountTimer);
                this.cropMountTimer = undefined;
            }
            this.setState({ cropperReady: false, croppedAreaPixels: undefined });
        }
        if (
            this.props.open &&
            prevProps.open &&
            (this.props.imageSrc !== prevProps.imageSrc || this.props.aspect !== prevProps.aspect)
        ) {
            this.scheduleCropperMount();
        }
    }

    scheduleCropperMount() {
        if (this.cropMountTimer != null) {
            window.clearTimeout(this.cropMountTimer);
        }
        this.setState({
            crop: { x: 0, y: 0 },
            zoom: 1,
            croppedAreaPixels: undefined,
            cropperReady: false,
        });
        this.cropMountTimer = window.setTimeout(() => {
            this.cropMountTimer = undefined;
            this.setState({ cropperReady: true });
        }, 250);
    }

    componentWillUnmount() {
        if (this.cropMountTimer != null) {
            window.clearTimeout(this.cropMountTimer);
        }
    }

    onCropComplete = (croppedArea: any, croppedAreaPixels: Area) => {
        this.setState({ croppedAreaPixels });
    };

    submit = async () => {
        if (!this.state.croppedAreaPixels) {
            window.alert('Тайрлын хэмжээ тооцоогдоогүй байна. Зургийг бага зэрэг хөдөлгөөд дахин оролдоно уу.');
            return;
        }
        if (this.state.submitting) {
            return;
        }
        this.setState({ submitting: true });
        try {
            let w: number;
            let h: number;
            if (this.props.outputWidth && this.props.outputHeight) {
                w = this.props.outputWidth;
                h = this.props.outputHeight;
            } else {
                const s = this.props.outputSize || 800;
                w = s;
                h = s;
            }
            const blob = await getCroppedBlob(
                this.props.imageSrc,
                this.state.croppedAreaPixels,
                w,
                h
            );
            const picked = this.props.sourceFileSizeBytes;
            const cropped = blob.size;
            if (typeof picked === 'number' && picked > 0) {
                const ratio = cropped / picked;
                const pct = (1 - ratio) * 100;
                console.log('[ImageCropper] picked file vs cropped JPEG (before S3)', {
                    pickedFileBytes: picked,
                    croppedJpegBytes: cropped,
                    ratioCroppedToPicked: Number(ratio.toFixed(4)),
                    vsPickedFile: pct >= 0 ? `${pct.toFixed(1)}% smaller` : `${(-pct).toFixed(1)}% larger`,
                });
            } else {
                console.log('[ImageCropper] cropped JPEG bytes (before S3)', { croppedJpegBytes: cropped });
            }
            await Promise.resolve(this.props.onCropped(blob));
        } catch (err) {
            console.error('ImageCropperModal onCropped:', err);
        } finally {
            this.setState({ submitting: false });
        }
    };

    handleClose = () => {
        if (this.state.submitting) {
            return;
        }
        this.props.onClose();
    };

    render() {
        const { open, imageSrc, cropTitle } = this.props;
        const aspect = this.props.aspect != null ? this.props.aspect : 1;
        const header =
            cropTitle ||
            (aspect === 4
                ? 'Зураг тайрах (4:1)'
                : Math.abs(aspect - 4 / 3) < 0.0001
                  ? 'Зураг тайрах (4:3)'
                  : aspect === 1
                    ? 'Зураг тайрах (800×800)'
                    : 'Зураг тайрах');
        const submitting = this.state.submitting;
        return (
            <Modal
                open={open}
                onClose={this.handleClose}
                size="fullscreen"
                closeIcon
                closeOnDimmerClick={!submitting}
                closeOnEscape={!submitting}
            >
                <Modal.Header>{header}</Modal.Header>
                <Modal.Content scrolling style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 220px)' }}>
                    <div
                        style={{
                            position: 'relative',
                            width: 'min(96vw, 1100px)',
                            height: 'min(70vh, 1100px)',
                            maxWidth: '100%',
                            background: '#111',
                            opacity: submitting ? 0.55 : 1,
                            pointerEvents: submitting ? 'none' : 'auto',
                        }}
                    >
                        {this.state.cropperReady ? (
                            <Cropper
                                key={`${imageSrc}-${aspect}`}
                                image={imageSrc}
                                crop={this.state.crop}
                                zoom={this.state.zoom}
                                aspect={aspect}
                                cropShape="rect"
                                showGrid={false}
                                onCropChange={(crop: any) => this.setState({ crop })}
                                onZoomChange={(zoom: any) => this.setState({ zoom })}
                                onCropComplete={this.onCropComplete}
                            />
                        ) : (
                            <div style={{ padding: '2rem', color: '#888' }}>Ачаалж байна…</div>
                        )}
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <Button type="button" onClick={this.handleClose} disabled={submitting} loading={false}>
                        Болих
                    </Button>
                    <Button primary type="button" onClick={this.submit} loading={submitting} disabled={submitting}>
                        {submitting ? 'Илгээж байна…' : 'Тайраад ашиглах'}
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }
}

